import { Activity, BookOpen, CalendarClock, History, LineChart, LogOut, Server, Settings, User, Zap } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertsBell } from "@/components/AlertsBell";
import { Masked } from "@/components/Masked";
import { MobileTabs } from "@/components/MobileTabs";

export const TRADER_NAV = [
  { to: "/trade", label: "Trade", icon: Zap, testid: "nav-trade" },
  { to: "/positions", label: "Positions", icon: LineChart, testid: "nav-positions" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
];

export const Shell = ({ children, status, onProfile }) => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = user?.role === "admin";
  const eaDown = status?.mode === "offline";
  const connected = status?.connected && !eaDown;

  const navCls = (active) =>
    `flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200 ${
      active
        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-700/60"
        : "text-zinc-300 hover:bg-zinc-800 hover:text-white border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-[#08090A] text-zinc-100">
      <header className="sticky top-0 z-40 border-b border-zinc-800 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <Link to={isAdmin ? "/admin" : "/trade"} className="flex items-center gap-2 mr-2">
            <Logo size={24} />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {!isAdmin &&
              TRADER_NAV.map(({ to, label, icon: Icon, testid }) => (
                <Link key={to} to={to} className={navCls(pathname === to)} data-testid={testid}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </Link>
              ))}
            {isAdmin && (
              <>
                <Link to="/admin" className={navCls(pathname === "/admin")} data-testid="nav-admin">
                  <Settings className="h-3.5 w-3.5" /> Admin
                </Link>
                <Link to="/admin/pods" className={navCls(pathname === "/admin/pods")} data-testid="nav-pods">
                  <Server className="h-3.5 w-3.5" /> Infra
                </Link>
                <Link to="/admin/leads" className={navCls(pathname === "/admin/leads")} data-testid="nav-leads">
                  <CalendarClock className="h-3.5 w-3.5" /> Leads
                </Link>
                <Link to="/admin/logic" className={navCls(pathname === "/admin/logic")} data-testid="nav-logic">
                  <BookOpen className="h-3.5 w-3.5" /> Logic
                </Link>
              </>
            )}
          </nav>
          <div className="flex-1" />
          {isAdmin ? (
            <div
              data-testid="mt5-status"
              className="flex items-center gap-2 border border-emerald-800 bg-emerald-500/10 text-emerald-300 px-3 py-1.5 rounded-sm text-xs font-mono"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              MANAGER MODE · NO TRADING
            </div>
          ) : (
            <div
              data-testid="mt5-status"
              className={`flex items-center gap-2 border px-3 py-1.5 rounded-sm text-xs font-mono ${
                connected
                  ? "border-emerald-700 bg-emerald-500/10 text-emerald-300"
                  : "border-red-800 bg-red-500/10 text-red-300"
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  connected ? "bg-emerald-400 animate-pulse" : "bg-red-500"
                }`}
              />
              {eaDown ? "EA OFFLINE" : connected ? "MT5 CONNECTED" : "MT5 OFFLINE"}
              {!eaDown && status?.mode === "simulator" && (
                <span className="text-zinc-300">· SIM</span>
              )}
            </div>
          )}
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-xs text-zinc-300 font-mono" data-testid="user-role">
                {user?.email && (
                  <Masked value={user.email} kind="email" testid="shell-user-email" />
                )}{" "}
                · {user?.role?.toUpperCase()}
              </span>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="p-2 text-zinc-300 hover:text-red-300 hover:bg-zinc-900 rounded-sm transition-colors duration-200"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AlertsBell />
              <button
                data-testid="profile-btn"
                onClick={onProfile}
                title="Profile, MT5 and preferences"
                className="h-9 w-9 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center text-zinc-200 hover:border-emerald-600 hover:text-emerald-300 transition-colors duration-200"
              >
                <User className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto p-4 sm:p-6 pb-28 md:pb-6">{children}</main>
      <MobileTabs />
    </div>
  );
};
